import React, { useEffect, useRef } from 'react';

/**
 * Continuous-festival fireworks animation rendered via canvas.
 * Spawns multiple overlapping bursts in red/white/blue at all times.
 * Renders only when mounted; the parent controls visibility.
 */

const COLORS = [
  [230, 57, 70],   // red
  [255, 100, 112], // light red
  [255, 255, 255], // white
  [74, 141, 255],  // blue
  [130, 177, 255], // light blue
  [255, 245, 220], // warm white
];

const rand = (min, max) => Math.random() * (max - min) + min;
const choice = (arr) => arr[Math.floor(Math.random() * arr.length)];

class Particle {
  constructor(x, y, color) {
    const angle = Math.random() * Math.PI * 2;
    const speed = rand(1.4, 5.6);
    this.x = x;
    this.y = y;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed;
    this.life = 1;
    this.decay = rand(0.012, 0.025);
    this.color = color;
    this.size = rand(1.5, 3);
    this.gravity = 0.05;
    this.drag = 0.985;
  }
  step() {
    this.x += this.vx;
    this.y += this.vy;
    this.vx *= this.drag;
    this.vy *= this.drag;
    this.vy += this.gravity;
    this.life -= this.decay;
  }
  draw(ctx) {
    if (this.life <= 0) return;
    const [r, g, b] = this.color;
    ctx.globalAlpha = Math.max(0, this.life);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
    // Slight glow
    ctx.globalAlpha = Math.max(0, this.life * 0.35);
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size * 2.6, 0, Math.PI * 2);
    ctx.fill();
  }
}

class Rocket {
  constructor(width, height) {
    this.x = rand(width * 0.06, width * 0.94);
    this.y = height + 10;
    this.targetY = rand(height * 0.12, height * 0.55);
    this.vy = -rand(7, 10);
    this.color = choice(COLORS);
    this.exploded = false;
    this.trail = [];
  }
  step() {
    this.trail.push({ x: this.x, y: this.y, life: 0.5 });
    if (this.trail.length > 8) this.trail.shift();
    this.trail.forEach((t) => { t.life -= 0.06; });
    this.y += this.vy;
    this.vy += 0.07;
    if (this.y <= this.targetY) this.exploded = true;
  }
  draw(ctx) {
    this.trail.forEach((t) => {
      if (t.life <= 0) return;
      const [r, g, b] = this.color;
      ctx.globalAlpha = Math.max(0, t.life);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.beginPath();
      ctx.arc(t.x, t.y, 2, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  explode(minParts, maxParts) {
    const particles = [];
    const count = Math.floor(rand(minParts ?? 60, maxParts ?? 110));
    for (let i = 0; i < count; i += 1) {
      particles.push(new Particle(this.x, this.y, this.color));
    }
    return particles;
  }
}

const Fireworks = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    // Mobile / touch / reduced-motion → skip fireworks entirely (perf).
    const isSmall = window.matchMedia('(max-width: 900px)').matches;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isSmall || prefersReduced) return undefined;

    // Desktop-only settings — tuned down to keep the frame budget cheap.
    const SPAWN_INTERVAL = 2400; // ~1 rocket every 2.4s (was 900ms)
    const MAX_PARTICLES = 240;   // hard cap (was 700)
    const EXPLODE_MIN = 24;
    const EXPLODE_MAX = 42;

    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = 1; // desktop: cap DPR at 1 for fireworks canvas — decorative only
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    let rockets = [];
    let particles = [];
    let raf;
    let lastSpawn = 0;
    let paused = document.hidden;

    const onVisibility = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVisibility);

    const loop = (t) => {
      // Pause work entirely when the tab is backgrounded — the browser
      // still ticks rAF slowly, but we should not paint or spawn.
      if (paused) {
        raf = requestAnimationFrame(loop);
        return;
      }

      // Trailing fade so motion blurs without clearing the universe
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(10, 22, 56, 0.18)';
      ctx.fillRect(0, 0, width, height);

      // Spawn — mobile-aware interval
      if (t - lastSpawn > SPAWN_INTERVAL) {
        rockets.push(new Rocket(width, height));
        lastSpawn = t;
      }

      // Step rockets
      const stillFlying = [];
      rockets.forEach((r) => {
        r.step();
        r.draw(ctx);
        if (r.exploded) {
          const explosion = r.explode(EXPLODE_MIN, EXPLODE_MAX);
          // Hard cap total particles to protect frame budget
          const room = MAX_PARTICLES - particles.length;
          if (room > 0) {
            particles = particles.concat(
              explosion.length <= room ? explosion : explosion.slice(0, room)
            );
          }
        } else {
          stillFlying.push(r);
        }
      });
      rockets = stillFlying;

      // Step particles
      particles = particles.filter((p) => p.life > 0);
      particles.forEach((p) => {
        p.step();
        p.draw(ctx);
      });

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fireworks-canvas"
      data-testid="fireworks-canvas"
      aria-hidden="true"
    />
  );
};

export default Fireworks;
